import React, { useState, useCallback } from 'react';
import { useApp } from '../context/AppContext';

const API = '/api';
const inputStyle = { width: '100%' };
const CURRENCIES = ['USD', 'AED', 'NGN', 'GBP', 'EUR'];
const AMENITIES_LIST = ['Changing Rooms', 'Showers', 'Parking', 'Floodlights', 'Artificial Turf', 'Natural Grass', 'Seating', 'Café / Canteen', 'First Aid', 'WiFi'];
const PITCH_TYPES = ['5v5', '7v7', '11v11', 'Multi-format'];

const INIT = {
  name: '', location: '', address: '', city: '',
  latitude: '', longitude: '', price: '', currency: 'AED',
  amenities: [], description: '', indoorOutdoor: 'Outdoor', pitchType: '',
};

function AmenityToggle({ amenity, selected, onToggle }) {
  return (
    <div onClick={() => onToggle(amenity)} style={{
      padding: '7px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
      border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
      background: selected ? 'rgba(167,244,50,0.1)' : 'transparent',
      color: selected ? 'var(--accent)' : 'var(--text-dim)',
    }}>{amenity}</div>
  );
}

export default function PitchCreation({ toast }) {
  const { token } = useApp();
  const [form, setForm] = useState(INIT);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  const setF = useCallback((k, v) => setForm(p => ({ ...p, [k]: v })), []);

  const toggleAmenity = useCallback((a) => {
    setForm(p => ({
      ...p,
      amenities: p.amenities.includes(a) ? p.amenities.filter(x => x !== a) : [...p.amenities, a]
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast?.('Pitch name is required', 'error'); return; }
    if (!form.location.trim()) { toast?.('Location is required', 'error'); return; }
    if (!form.price) { toast?.('Price is required', 'error'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(API + '/app/pitches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({
          ...form,
          price: parseFloat(form.price),
          latitude: form.latitude ? parseFloat(form.latitude) : null,
          longitude: form.longitude ? parseFloat(form.longitude) : null,
          availability: [],
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed to create pitch');
      setCreated(d.data || d);
      toast?.('Pitch created successfully!');
      setForm(INIT);
    } catch (err) { toast?.(err.message, 'error'); }
    finally { setSubmitting(false); }
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 500, color: 'var(--text-primary)', margin: 0 }}>Create Pitch</h1>
        <p style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 4 }}>Add a new pitch to the TransferExpo marketplace</p>
      </div>

      {created && (
        <div style={{ background: 'rgba(167,244,50,0.08)', border: '1px solid rgba(167,244,50,0.3)', borderRadius: 10, padding: '16px 20px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--accent)' }}>✅ {created.name} created!</div>
            <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>{created.city || created.location} · {created.currency} {created.price}</div>
          </div>
          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setCreated(null)}>Create another</button>
        </div>
      )}

      <form className="card" onSubmit={handleSubmit} style={{ padding: '24px 28px' }}>
        <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 20 }}>Pitch details</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Pitch name *</label>
            <input className="search-input" style={inputStyle} value={form.name} onChange={e => setF('name', e.target.value)} placeholder="e.g. Dubai Football Court" />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>City</label>
            <input className="search-input" style={inputStyle} value={form.city} onChange={e => setF('city', e.target.value)} placeholder="e.g. Dubai" />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Location / Area *</label>
          <input className="search-input" style={inputStyle} value={form.location} onChange={e => setF('location', e.target.value)} placeholder="e.g. Al Quoz, Dubai" />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Full address</label>
          <input className="search-input" style={inputStyle} value={form.address} onChange={e => setF('address', e.target.value)} placeholder="Street address" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Indoor / Outdoor</label>
            <select className="search-input" style={inputStyle} value={form.indoorOutdoor} onChange={e => setF('indoorOutdoor', e.target.value)}>
              <option value="Outdoor">Outdoor</option>
              <option value="Indoor">Indoor</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Pitch Format</label>
            <select className="search-input" style={inputStyle} value={form.pitchType} onChange={e => setF('pitchType', e.target.value)}>
              <option value="">Any format</option>
              {PITCH_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Description</label>
          <textarea className="search-input" style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} rows={3}
            value={form.description} onChange={e => setF('description', e.target.value)}
            placeholder="Describe the pitch — surface type, facilities, rules..." />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Latitude</label>
            <input className="search-input" style={inputStyle} type="number" step="any" value={form.latitude} onChange={e => setF('latitude', e.target.value)} placeholder="e.g. 25.1972" />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Longitude</label>
            <input className="search-input" style={inputStyle} type="number" step="any" value={form.longitude} onChange={e => setF('longitude', e.target.value)} placeholder="e.g. 55.2744" />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Price per booking *</label>
            <input className="search-input" style={inputStyle} type="number" min={0} step="0.01" value={form.price} onChange={e => setF('price', e.target.value)} placeholder="e.g. 150" />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Currency</label>
            <select className="search-input" style={inputStyle} value={form.currency} onChange={e => setF('currency', e.target.value)}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 10, textTransform: 'uppercase' }}>Amenities</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {AMENITIES_LIST.map(a => (
              <AmenityToggle key={a} amenity={a} selected={form.amenities.includes(a)} onToggle={toggleAmenity} />
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={() => setForm(INIT)} disabled={submitting}>Clear</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create pitch'}
          </button>
        </div>
      </form>
    </div>
  );
}
